import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

const SUBTLE = {
  info: 'bg-info-subtle text-info border-info-muted',
  success: 'bg-success-subtle text-success border-success-muted',
  warning: 'bg-warning-subtle text-warning border-warning-muted',
  danger: 'bg-danger-subtle text-danger border-danger-muted',
  neutral: 'bg-muted text-muted border-default',
};

// Il contatore pieno (notifiche): riempimento del tono, testo chiaro, forma compatta.
const COUNT = {
  info: 'bg-info-emphasis',
  success: 'bg-success-emphasis',
  warning: 'bg-warning-emphasis',
  danger: 'bg-danger-emphasis',
  neutral: 'bg-emphasis',
};

export type StatusTone = keyof typeof SUBTLE;

type StatusBadgeProps = {
  children: ReactNode;
  tone?: StatusTone;
  count?: boolean;
  icon?: LucideIcon;
  className?: string;
  title?: string;
};

/**
 * Lo stato di una riga o di una scheda, nella tinta del tono con il suo bordo; un
 * testo troppo lungo per il contenitore si tronca. `count` lo fa contatore pieno;
 * `icon` è un componente lucide.
 */
const StatusBadge = ({
  children,
  tone = 'neutral',
  count = false,
  icon: Icon,
  className,
  title,
}: StatusBadgeProps) => {
  const toneKey = tone in SUBTLE ? tone : 'neutral';

  return (
    <span
      title={title}
      className={cn(
        'inline-block max-w-full truncate rounded-full border align-middle text-badge',
        count
          ? `min-w-5 border-transparent px-1.5 py-0.5 text-center tabular-nums text-on-emphasis ${COUNT[toneKey]}`
          : `px-2.5 py-1 ${SUBTLE[toneKey]}`,
        className
      )}
    >
      {Icon ? <Icon aria-hidden className="-mt-px mr-1 inline size-3 align-middle" /> : null}
      {children}
    </span>
  );
};

export default StatusBadge;
